import { useState } from 'react';
import { Link } from 'react-router';
import {
  useCharacters,
  useArchiveCharacter,
  useUpdateCharacter,
  useElevenlabsVoices,
  type CharacterEntry,
  type ElevenLabsVoice,
} from '../hooks/useApi';
import { BASE } from '../api/client';
import { LivePreviewModal } from '../components/LivePreviewModal';
import { CharacterFormModal } from '../components/CharacterFormModal';

/**
 * Página `/character-creator` — galería GLOBAL de personajes reutilizables.
 *
 * Catálogo único de la app (decisión usuario 2026-04-25). NO depende de proyecto.
 * Cada personaje tiene una imagen (con 2 vistas: closeup + medium del prompt A1)
 * + voz asignada opcional. Reutilizable en pruebas de lipsync, generaciones futuras,
 * live preview, etc.
 *
 * Bootstrap: el backend siembra 6 personajes iniciales al startup
 * (CharacterGalleryBootstrapService) si la galería está vacía.
 */
export function CharacterCreatorPage() {
  const [includeArchived, setIncludeArchived] = useState(false);
  const [previewing, setPreviewing] = useState<CharacterEntry | null>(null);
  const [creating, setCreating] = useState(false);
  const [showFullView, setShowFullView] = useState<CharacterEntry | null>(null);

  // Galería GLOBAL — sin projectId
  const { data: characters, isLoading, error } = useCharacters(undefined, includeArchived);
  const { data: voices } = useElevenlabsVoices();
  const archive = useArchiveCharacter('');
  const update = useUpdateCharacter('');

  return (
    <div className="p-3 sm:p-6 max-w-6xl mx-auto">
      <header className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-2xl font-bold text-slate-100">👥 Character Creator</h1>
          <Link to="/projects" className="text-xs text-slate-400 hover:text-vision underline">
            ← volver a proyectos
          </Link>
        </div>
        <p className="text-sm text-slate-400">
          Galería global de personajes reutilizables. Mostrale al cliente cuáles podés usar para
          su proyecto. Cada uno tiene una ficha con closeup + medium shot, y una voz asignada
          (Pool A ElevenLabs por default).
        </p>
      </header>

      <section className="mb-6 flex flex-wrap items-center gap-3">
        <label className="text-xs text-slate-400 flex items-center gap-2">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
          />
          mostrar archivados
        </label>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-slate-500">
            {characters?.length ?? 0} personaje{characters?.length === 1 ? '' : 's'}
          </span>
          <button
            onClick={() => setCreating(true)}
            className="px-3 py-1.5 text-sm rounded bg-vision-dark hover:bg-vision text-white"
          >
            + Nuevo personaje
          </button>
        </div>
      </section>

      {isLoading && <div className="text-slate-400 text-sm">Cargando galería…</div>}

      {error && (
        <div className="text-red-400 text-sm">
          Error: {error instanceof Error ? error.message : String(error)}
        </div>
      )}

      {characters && characters.length === 0 && (
        <div className="text-center text-slate-500 py-12 text-sm">
          La galería está vacía.
          <br />
          El bootstrap del backend la puebla automáticamente con 6 personajes seed al primer
          startup. Si no aparecen, verificá los logs del API o creá uno con "+ Nuevo personaje".
        </div>
      )}

      {characters && characters.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {characters.map((c) => (
            <CharacterCard
              key={c.id}
              entry={c}
              voices={voices ?? []}
              onArchive={() => archive.mutate(c.id)}
              onLivePreview={() => setPreviewing(c)}
              onShowFull={() => setShowFullView(c)}
              onChangeVoice={(voiceId) =>
                update.mutate({
                  id: c.id,
                  input: {
                    voiceId: voiceId || null,
                    voiceProvider: voiceId ? 'elevenlabs' : null,
                  },
                })
              }
            />
          ))}
        </div>
      )}

      {previewing && (
        <LivePreviewModal character={previewing} onClose={() => setPreviewing(null)} />
      )}

      {creating && <CharacterFormModal onClose={() => setCreating(false)} />}

      {showFullView && (
        <FullViewModal entry={showFullView} onClose={() => setShowFullView(null)} />
      )}
    </div>
  );
}

function CharacterCard({
  entry,
  voices,
  onArchive,
  onLivePreview,
  onShowFull,
  onChangeVoice,
}: {
  entry: CharacterEntry;
  voices: ElevenLabsVoice[];
  onArchive: () => void;
  onLivePreview: () => void;
  onShowFull: () => void;
  onChangeVoice: (voiceId: string) => void;
}) {
  const thumbUrl = entry.asset ? `${BASE}/assets/${entry.asset.id}/thumb` : null;

  return (
    <div
      className={`border rounded-lg overflow-hidden bg-slate-900 hover:border-vision transition-colors flex flex-col ${
        entry.archived ? 'border-slate-700 opacity-60' : 'border-slate-700'
      }`}
    >
      <button
        onClick={onShowFull}
        className="aspect-square bg-slate-950 flex items-center justify-center overflow-hidden cursor-zoom-in"
        title="Click para ver imagen completa (con las 2 vistas)"
      >
        {thumbUrl ? (
          <img
            src={thumbUrl}
            alt={entry.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <span className="text-slate-700 text-4xl">👤</span>
        )}
      </button>
      <div className="p-3 flex flex-col gap-2 flex-1">
        <h3 className="text-sm font-semibold text-slate-100">{entry.name}</h3>
        {entry.description && (
          <p className="text-xs text-slate-400 line-clamp-2">{entry.description}</p>
        )}
        <div>
          <label className="text-[10px] text-slate-500 uppercase block mb-1">🎤 voz</label>
          <select
            value={entry.voiceId ?? ''}
            onChange={(e) => onChangeVoice(e.target.value)}
            className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs text-slate-200 focus:outline-none focus:border-vision"
          >
            <option value="">— sin voz asignada —</option>
            <optgroup label="🎭 Voces clonadas / profesionales">
              {voices
                .filter((v) => v.isCloned)
                .map((v) => (
                  <option key={v.voice_id} value={v.voice_id}>
                    {v.name}
                  </option>
                ))}
            </optgroup>
            <optgroup label="📚 Voces públicas">
              {voices
                .filter((v) => !v.isCloned)
                .map((v) => (
                  <option key={v.voice_id} value={v.voice_id}>
                    {v.name}
                  </option>
                ))}
            </optgroup>
          </select>
        </div>
        {entry.archived && (
          <div className="text-[10px] text-amber-400/70 italic">archivado</div>
        )}
        <div className="flex gap-1 mt-auto pt-2">
          <button
            onClick={onLivePreview}
            disabled={!entry.voiceId}
            className="flex-1 px-2 py-1 text-xs rounded bg-vision/20 hover:bg-vision/40 text-vision border border-vision/40 disabled:opacity-40 disabled:cursor-not-allowed"
            title={!entry.voiceId ? 'Asigná una voz primero' : 'Live preview con TTS'}
          >
            🎬 Preview
          </button>
          {!entry.archived && (
            <button
              onClick={onArchive}
              className="px-2 py-1 text-xs rounded bg-slate-800 hover:bg-red-900/40 text-slate-400 hover:text-red-300 border border-slate-700"
              title="Archivar"
            >
              🗑
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** Modal que muestra la imagen completa (closeup + medium shot del prompt A1). */
function FullViewModal({ entry, onClose }: { entry: CharacterEntry; onClose: () => void }) {
  const fullUrl = entry.asset ? `${BASE}/assets/${entry.asset.id}/proxy` : null;
  return (
    <div
      className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-base text-slate-100 font-semibold">{entry.name}</h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-200 text-lg leading-none"
          >
            ✕
          </button>
        </div>
        {fullUrl && (
          <img
            src={fullUrl}
            alt={entry.name}
            className="w-full h-auto max-h-[85vh] object-contain rounded border border-slate-700"
          />
        )}
        <div className="text-[10px] text-slate-500 mt-2 text-center">
          Ficha A1: closeup (izquierda) + medium shot (derecha) — para usar como referencia
        </div>
      </div>
    </div>
  );
}
